import { Boundaries } from "@planzone/core";
import { gantt, Identifiable, List } from "@planzone/dto";
import { Event, events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { calculateAdvancement } from "../../utils/calculateAdvancement";
import { reduceGantt } from "../../common/gantt";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = {
  request: {
    isPending: false,
    isSuccessful: false,
    isFailure: false,
  },
  gantt,
};

function calculateTasksAdvancement(
  list: Identifiable<List>,
): Identifiable<List> {
  return {
    ...list,
    tasks: list.tasks.map(calculateAdvancement),
    subLists: list.subLists.map(calculateTasksAdvancement),
  };
}

function handleRequest(state: State): State {
  return { ...state, request: setRequestStateToPending() };
}

function handleSuccess(
  state: State,
  { gantt }: Boundaries["retrieveZonesProjectsGantt"]["success"],
): State {
  return {
    ...state,
    request: setRequestStateToSuccess(),
    gantt: {
      ...gantt,
      projects: gantt.projects.map((project) => ({
        ...project,
        lists: project.lists.map(calculateTasksAdvancement),
      })),
    },
  };
}

function handleFailure(state: State): State {
  return { ...state, request: setRequestStateToFailure() };
}

function propagateEvent(state = initialState, event: Event): State {
  return {
    ...state,
    gantt: reduceGantt(state.gantt, event),
  };
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrieveZonesProjectsGanttRequested]: handleRequest,
      [events.retrieveZonesProjectsGanttSucceeded]: handleSuccess,
      [events.retrieveZonesProjectsGanttFailed]: handleFailure,
    },
    propagateEvent,
  ),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
